import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Copy, ExternalLink, FileText, Link2, Loader2, Plus, Sparkles, Trash2, Upload,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import NumaSelect from "@/components/ui/numa-select";
import { bibliography, normalizeError } from "@/lib/api";
import { toast } from "sonner";
import { useLLM } from "@/contexts/LLMContext";

/**
 * Bibliography built from authoritative metadata rather than guesswork.
 *
 * A DOI resolved against Crossref, or a BibTeX/RIS export from the reference
 * manager the author already uses, gives correct data for free. Rendering goes
 * through real CSL styles, so APA and MLA actually differ.
 */

// How each record was obtained, so a deduced entry is never mistaken for a
// verified one. Green means the data came from an authoritative catalogue or
// from the author's own export; amber means it was read and should be checked.
export const ORIGIN_LABEL = {
  crossref: { text: "verificato su Crossref", tone: "text-ok" },
  doi: { text: "verificato su Crossref", tone: "text-ok" },
  importato: { text: "importato dal tuo file", tone: "text-ok" },
  "metadati del file": { text: "dai metadati del PDF", tone: "text-ink-muted" },
  "letto dal documento": { text: "letto dall'AI · da verificare", tone: "text-warn" },
  "letto dal testo": { text: "letto dall'AI · da verificare", tone: "text-warn" },
  euristica: { text: "dedotto · da verificare", tone: "text-warn" },
};

export function originMeta(origin) {
  if (origin?.startsWith("import:")) return { text: "importato", tone: "text-ink-subtle" };
  return ORIGIN_LABEL[origin] || { text: origin || "", tone: "text-ink-subtle" };
}

export default function BibliographyPanel({ thesis, reloadToken = 0 }) {
  const { showError } = useLLM();
  const thesisId = thesis?.thesis_id;
  const [styles, setStyles] = useState([]);
  const [style, setStyle] = useState("APA");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doi, setDoi] = useState("");
  const [busy, setBusy] = useState("");
  const [pasted, setPasted] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const fileRef = useRef(null);
  const existingRef = useRef(null);

  const load = useCallback(
    async (nextStyle = style) => {
      if (!thesisId) return;
      setLoading(true);
      try {
        const data = await bibliography.list(thesisId, nextStyle);
        setEntries(data.entries || []);
      } catch (err) {
        showError(normalizeError(err));
      } finally {
        setLoading(false);
      }
    },
    [thesisId, style, showError]
  );

  useEffect(() => {
    if (!thesisId) return;
    bibliography
      .styles(thesisId)
      .then((d) => setStyles(d.styles || []))
      .catch(() => setStyles([]));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thesisId]);

  // An extraction started from the library above writes new records; this is
  // how they appear here without a page reload.
  useEffect(() => {
    if (reloadToken) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  const changeStyle = async (next) => {
    setStyle(next);
    await load(next);
  };

  const addDoi = async (e) => {
    e.preventDefault();
    const value = doi.trim();
    if (!value) return;
    setBusy("doi");
    try {
      const res = await bibliography.addDoi(thesisId, value, style);
      setDoi("");
      await load();
      toast.success("Riferimento aggiunto", {
        description: res.open_access ? "Disponibile in open access." : undefined,
      });
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setBusy("");
    }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("import");
    try {
      const res = await bibliography.importFile(thesisId, file);
      await load();
      toast.success(`${res.parsed} riferimenti letti`, {
        description: `${res.added} aggiunti, ${res.updated} aggiornati.`,
      });
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setBusy("");
    }
  };

  const enrich = async () => {
    setBusy("enrich");
    try {
      const res = await bibliography.enrich(thesisId);
      await load();
      const parts = [];
      if (res.resolved) parts.push(`${res.resolved} da Crossref`);
      if (res.from_metadata) parts.push(`${res.from_metadata} dai metadati`);
      if (res.by_model) parts.push(`${res.by_model} lette dall'AI`);
      toast.success(`${res.processed} fonti elaborate`, {
        description: parts.join(" · ") || "Nessun dato bibliografico ricavabile.",
      });
      if (res.needs_model) {
        toast.info("Alcune fonti non hanno un DOI", {
          description: "Collega un modello per farle leggere a NUMA.",
        });
      }
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setBusy("");
    }
  };

  /**
   * A bibliography the author already wrote, in whatever shape.
   *
   * A .bib or .ris export is parsed outright; anything else - a list pasted
   * from Word, a PDF of references - is split into fields by the model, because
   * that is a reading task and the previous regular expressions got it wrong.
   */
  const convertExisting = async ({ file, text }) => {
    setBusy("convert");
    try {
      const res = await bibliography.parseExisting(thesisId, { file, text });
      await load();
      setPasted("");
      setShowPaste(false);
      toast.success(`${res.parsed} riferimenti convertiti`, {
        description: res.used_model
          ? "Letti dall'AI: controllali prima di consegnare."
          : "Letti direttamente dal file, senza usare l'AI.",
      });
    } catch (err) {
      const e = normalizeError(err);
      toast.error(e.message, { description: e.action || undefined });
    } finally {
      setBusy("");
    }
  };

  const remove = async (recordId) => {
    setBusy(recordId);
    try {
      await bibliography.remove(thesisId, recordId);
      setEntries((list) => list.filter((x) => x.record_id !== recordId));
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setBusy("");
    }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(entries.map((e) => e.entry).join("\n\n"));
      toast.success("Bibliografia copiata negli appunti");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  if (!thesisId) return null;

  return (
    <section id="bibliografia" data-testid="bibliography-panel" className="space-y-5 scroll-mt-6">
      <header className="flex flex-wrap items-end justify-between gap-x-10 gap-y-4">
        <div>
          <h2 className="font-serif-display text-[19px] font-semibold text-ink">Bibliografia</h2>
          <p className="numa-meta mt-1">
            Metadati da Crossref e dai tuoi file. Nessun modello, nessun costo.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="bib-style" className="text-2xs">Stile</Label>
            <NumaSelect
              id="bib-style"
              testId="bib-style-select"
              aria-label="Stile bibliografico"
              value={style}
              onValueChange={changeStyle}
              className="h-9 min-w-[9rem] text-[13px]"
              options={styles.map((s) => ({ value: s.id, label: s.label }))}
            />
          </div>
          <Button variant="outline" size="sm" onClick={copyAll} disabled={!entries.length}>
            <Copy className="h-3.5 w-3.5" /> Copia
          </Button>
        </div>
      </header>

      {/* Four ways in, each saying what it does and whether it needs a model. */}
      <div className="grid gap-2.5 sm:grid-cols-2">
        <form onSubmit={addDoi} className="rounded-md border border-line bg-surface p-3.5">
          <p className="flex items-center gap-1.5 text-[13.5px] font-medium text-ink">
            <Link2 className="h-3.5 w-3.5 text-ink-muted" /> Ho il DOI
          </p>
          <p className="numa-meta mt-0.5 leading-snug">
            Il modo più preciso: NUMA scarica i dati esatti da Crossref. Senza AI.
          </p>
          <div className="mt-2.5 flex gap-2">
            <Input
              value={doi}
              onChange={(e) => setDoi(e.target.value)}
              placeholder="10.1038/s41586-021-03819-2"
              aria-label="DOI da aggiungere"
              className="h-9 font-mono text-[12.5px]"
              data-testid="doi-input"
            />
            <Button type="submit" size="sm" disabled={busy === "doi" || !doi.trim()}>
              {busy === "doi" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Aggiungi
            </Button>
          </div>
        </form>

        <div className="rounded-md border border-line bg-surface p-3.5">
          <p className="flex items-center gap-1.5 text-[13.5px] font-medium text-ink">
            <Upload className="h-3.5 w-3.5 text-ink-muted" /> Uso Zotero o Mendeley
          </p>
          <p className="numa-meta mt-0.5 leading-snug">
            Esporta in .bib o .ris e caricalo: i dati sono già corretti. Senza AI.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2.5"
            onClick={() => fileRef.current?.click()}
            disabled={busy === "import"}
          >
            {busy === "import" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Scegli il file
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".bib,.ris"
            onChange={importFile}
            className="hidden"
            data-testid="bib-import-input"
          />
        </div>

        <div className="rounded-md border border-line bg-surface p-3.5">
          <p className="flex items-center gap-1.5 text-[13.5px] font-medium text-ink">
            <Sparkles className="h-3.5 w-3.5 text-assistant" /> Come citare le fonti che ho caricato
          </p>
          <p className="numa-meta mt-0.5 leading-snug">
            Un riferimento per ogni file della tua biblioteca: dal DOI se ce l&apos;ha, dai
            metadati se li ha, altrimenti facendo leggere il frontespizio al modello. Per
            prendere invece <em>la bibliografia contenuta in un file</em>, usa il pulsante
            sotto quel file.
          </p>
          <Button variant="outline" size="sm" className="mt-2.5" onClick={enrich} disabled={busy === "enrich"}>
            {busy === "enrich" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Ricava dalle fonti
          </Button>
        </div>

        <div className="rounded-md border border-line bg-surface p-3.5">
          <p className="flex items-center gap-1.5 text-[13.5px] font-medium text-ink">
            <FileText className="h-3.5 w-3.5 text-assistant" /> Ho già una bibliografia scritta
          </p>
          <p className="numa-meta mt-0.5 leading-snug">
            Incollala o carica il file: il modello la scompone in autore, titolo e anno, così
            puoi riformattarla in qualsiasi stile.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPaste((v) => !v)}
              disabled={busy === "convert"}
            >
              {showPaste ? "Chiudi" : "Incolla il testo"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => existingRef.current?.click()}
              disabled={busy === "convert"}
            >
              {busy === "convert" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Carica un file
            </Button>
            <input
              ref={existingRef}
              type="file"
              accept=".pdf,.docx,.txt,.bib,.ris"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) convertExisting({ file });
              }}
              className="hidden"
              data-testid="bib-existing-input"
            />
          </div>
        </div>
      </div>

      {showPaste && (
        <div className="rounded-md border border-line bg-surface p-3.5">
          <Textarea
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder={"Rossi, M. (2019). Titolo dell'articolo. Rivista, 12(3), 45-67.\nBianchi, L. (2021). Titolo del libro. Editore."}
            className="min-h-[130px] resize-none font-mono text-[12.5px]"
            data-testid="bib-paste-area"
          />
          <div className="mt-2 flex justify-end">
            <Button
              size="sm"
              onClick={() => convertExisting({ text: pasted })}
              disabled={busy === "convert" || pasted.trim().length < 20}
            >
              {busy === "convert" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Converti
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="numa-shimmer h-10 rounded-md" />)}
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-md border border-dashed border-line-strong p-6 text-center">
          <p className="text-[13.5px] text-ink">Nessun riferimento.</p>
          <p className="numa-meta mt-1 leading-relaxed">
            Incolla un DOI, importa il file di Zotero o Mendeley, oppure lascia che
            NUMA legga i metadati dai PDF che hai caricato.
          </p>
        </div>
      ) : (
        <ol className="space-y-0">
          {entries.map((item, i) => {
            const meta = originMeta(item.origin);
            return (
              <li
                key={item.record_id}
                className="group flex gap-3 border-b border-line py-2.5 last:border-0"
              >
                <span className="w-5 shrink-0 pt-0.5 text-2xs tabular-nums text-ink-subtle">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-serif-display text-[14px] leading-relaxed text-ink">{item.entry}</p>
                  <p className="mt-1 flex flex-wrap items-center gap-2.5 text-2xs">
                    <span className={meta.tone}>{meta.text}</span>
                    {item.doi && (
                      <a
                        href={`https://doi.org/${item.doi}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-brand hover:underline"
                      >
                        {item.doi} <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(item.record_id)}
                  disabled={busy === item.record_id}
                  title="Rimuovi"
                  className="h-6 shrink-0 self-start rounded px-1.5 text-ink-subtle transition-colors hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="sr-only">Rimuovi riferimento</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      <p className="mt-2 border-t border-line pt-5 text-2xs leading-relaxed text-ink-subtle">
        Risolvendo un DOI, NUMA interroga Crossref e OpenAlex: viaggia solo l&apos;identificativo,
        mai il testo del tuo lavoro. L&apos;import da file e la formattazione avvengono
        interamente sul tuo dispositivo.
      </p>
    </section>
  );
}
